import React, { useEffect, useRef, useState } from 'react';

const PaymentsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Phone animation - 6 seconds per step
  useEffect(() => {
    if (!isVisible) return;
    
    const STEP_DURATION = 6000;
    
    const interval = setInterval(() => {
      setAnimationStep(prev => (prev + 1) % 6);
    }, STEP_DURATION);
    
    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <section 
      className="payments-section" 
      ref={sectionRef}
      data-testid="payments-section"
    >
      <div className="payments-content">
        <div className="payments-layout">
          {/* Left Content */}
          <div className="payments-left">
            <div className={`section-header-inline ${isVisible ? 'visible' : ''}`}>
              <span className="section-number-small">03</span>
              <p className="section-label">Payments</p>
              <h2 className="section-title">Crypto-Native Payments</h2>
              <p className="section-description">
                No cards. No stored billing data. No breach vectors. Pay anonymously with cryptocurrency 
                and maintain complete financial privacy while accessing premium AI services.
              </p>
            </div>

            <div className={`payments-cards ${isVisible ? 'visible' : ''}`}>
              <div className="payment-card" style={{ animationDelay: '0.1s' }}>
                <div className="card-number">01</div>
                <div className="card-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                </div>
                <h4>Pay with Crypto</h4>
                <p>Use BTC, ETH, USDC or any supported token</p>
              </div>
              <div className="payment-card" style={{ animationDelay: '0.2s' }}>
                <div className="card-number">02</div>
                <div className="card-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3"/>
                  </svg>
                </div>
                <h4>ZyberSEC Converts</h4>
                <p>Instant conversion to service credits</p>
              </div>
              <div className="payment-card" style={{ animationDelay: '0.3s' }}>
                <div className="card-number">03</div>
                <div className="card-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <h4>AI Gets Paid</h4>
                <p>Your identity stays completely private</p>
              </div>
            </div>

            <div className="payments-stats">
              <div className="stat-item">
                <span className="stat-value">0</span>
                <span className="stat-label">Cards Stored</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">100%</span>
                <span className="stat-label">Private</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">Instant</span>
                <span className="stat-label">Convert</span>
              </div>
            </div>
          </div>

          {/* Right: Phone Mockup */}
          <div className="payments-right">
            <div className={`phone-mockup-wrapper ${isVisible ? 'visible' : ''}`}>
              <div className="phone-frame-new">
                <div className="phone-notch-new"></div>
                <div className="phone-screen-new">
                  {/* Balance */}
                  <div className="app-balance-new">
                    <span className="balance-label-new">Crypto Balance</span>
                    <span className="balance-amount-new">
                      {animationStep >= 3 && animationStep < 5 ? '$150' : animationStep >= 5 ? '$130' : '$50'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="app-actions-new">
                    <div className={`action-btn-new ${animationStep === 0 ? 'active' : ''}`}>
                      <div className="action-icon-new">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2v20M2 12h20"/>
                        </svg>
                      </div>
                      <span>Deposit</span>
                    </div>
                    <div className="action-btn-new">
                      <div className="action-icon-new">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                        </svg>
                      </div>
                      <span>Send</span>
                    </div>
                    <div className={`action-btn-new ${animationStep === 4 ? 'active' : ''}`}>
                      <div className="action-icon-new">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3"/>
                        </svg>
                      </div>
                      <span>Convert</span>
                    </div>
                  </div>

                  {/* QR Popup */}
                  {(animationStep === 1 || animationStep === 2) && (
                    <div className="qr-popup-new">
                      <div className="qr-popup-content-new">
                        <div className="qr-header-new">
                          <span>Deposit ETH</span>
                          <div className="qr-close-new">×</div>
                        </div>
                        <div className="qr-code-box-new">
                          <svg viewBox="0 0 100 100" className="qr-code-svg-new">
                            <rect x="10" y="10" width="25" height="25" fill="#000"/>
                            <rect x="65" y="10" width="25" height="25" fill="#000"/>
                            <rect x="10" y="65" width="25" height="25" fill="#000"/>
                            <rect x="40" y="40" width="20" height="20" fill="#000"/>
                          </svg>
                        </div>
                        <div className="qr-address-new">
                          <span className="address-text-new">0x7a2B...f8E3</span>
                          <button className="copy-btn-new">Copy</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Deposit Confirmed */}
                  {animationStep === 3 && (
                    <div className="deposit-confirmed-new">
                      <div className="confirm-icon-new">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                      </div>
                      <span className="confirm-text-new">Deposit Confirmed!</span>
                      <span className="confirm-amount-new">+$100 ETH</span>
                    </div>
                  )}

                  {/* Convert */}
                  {animationStep === 4 && (
                    <div className="convert-ui-new">
                      <div className="convert-from-new">
                        <span>From: ETH</span>
                        <span className="convert-value-new">$100</span>
                      </div>
                      <div className="convert-arrow-new">↓</div>
                      <div className="convert-to-new">
                        <span>To: USD Credits</span>
                        <span className="convert-value-new">$100</span>
                      </div>
                    </div>
                  )}

                  {/* Purchase */}
                  {animationStep === 5 && (
                    <div className="purchase-ui-new">
                      <div className="purchase-header-new">Purchase AI Credits</div>
                      <div className="purchase-item-new active-purchase-new">
                        <div className="purchase-icon-new">AI</div>
                        <div className="purchase-info-new">
                          <span className="purchase-name-new">AI Subscription</span>
                          <span className="purchase-desc-new">Pro Plan - Monthly</span>
                        </div>
                        <span className="purchase-price-new">$20</span>
                      </div>
                      <div className="purchase-success-new">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                        <span>Complete!</span>
                      </div>
                    </div>
                  )}

                  {/* Holdings */}
                  {animationStep === 0 && (
                    <div className="holdings-new">
                      <div className="holding-item-new">
                        <div className="holding-icon-new eth">ETH</div>
                        <div className="holding-info-new">
                          <span className="holding-name-new">Ethereum</span>
                          <span className="holding-amount-new">0.015 ETH</span>
                        </div>
                        <span className="holding-value-new">$35</span>
                      </div>
                      <div className="holding-item-new">
                        <div className="holding-icon-new usdc">USDC</div>
                        <div className="holding-info-new">
                          <span className="holding-name-new">USD Coin</span>
                          <span className="holding-amount-new">15 USDC</span>
                        </div>
                        <span className="holding-value-new">$15</span>
                      </div>
                    </div>
                  )}

                  {/* Nav */}
                  <div className="app-nav-new">
                    <div className="nav-item-new active">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                      </svg>
                    </div>
                    <div className="nav-item-new">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 20V10M12 20V4M6 20v-6"/>
                      </svg>
                    </div>
                    <div className="nav-item-new">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaymentsSection;
